<template>
    <div class="domain-list-input">
        <div
            v-for="(domain, index) in modelValue"
            :key="index"
            class="domain-item mb-2"
        >
            <DInputGroup>
                <DInput
                    :model-value="domain"
                    add-input-classes="bg-light"
                    :placeholder="placeholderFor(index)"
                    :is-valid="isValid(index)"
                    :invalid-feedback="errorFor(index)"
                    @update:model-value="updateAt(index, $event)"
                />
                <DButton
                    icon="close"
                    color="danger"
                    @click="removeAt(index)"
                />
            </DInputGroup>
        </div>
        <DInputGroup>
            <DInput
                v-model="newDomain"
                add-input-classes="bg-light"
                :placeholder="resolvedAddPlaceholder"
                @keyup.enter="commitNew"
            />
            <DButton
                icon="plus"
                color="primary"
                :label="resolvedAddLabel"
                @click="commitNew"
            />
        </DInputGroup>
    </div>
</template>

<script>
/**
 * Editable list of domain strings — an add input plus a list of
 * existing entries each with an X button. Reused by the "create
 * certificate" modal and the "manage SANs" modal so both surfaces
 * share the same affordance for collecting one-or-more FQDNs.
 *
 * The component is a controlled list: callers own the array via
 * `v-model` and pass per-row validation errors through `errors`.
 */
export default {
    name: 'DomainListInput',

    props: {
        modelValue: {
            type: Array,
            required: true
        },
        /**
         * Per-index validation messages. Keys are stringified indexes
         * matching `modelValue`. The component renders the message
         * under each row when present.
         */
        errors: {
            type: Object,
            default: () => ({})
        },
        addPlaceholder: {
            type: String,
            default: null
        },
        addLabel: {
            type: String,
            default: null
        },
        /**
         * Returns the placeholder for a list-row input. Allows the
         * caller to label the first row differently (e.g. "Common
         * Name (primary)" in create mode). Null falls back to the
         * translated generic "Domain" placeholder below.
         */
        rowPlaceholder: {
            type: Function,
            default: null
        }
    },

    emits: ['update:modelValue'],

    data () {
        return {
            newDomain: ''
        }
    },

    computed: {
        resolvedAddPlaceholder () {
            return this.addPlaceholder ?? this.$t('certificate_domains.add_domain_placeholder')
        },

        resolvedAddLabel () {
            return this.addLabel ?? this.$t('common.add')
        }
    },

    methods: {
        commitNew () {
            const value = this.newDomain.trim()
            if (value === '') {
                return
            }
            this.$emit('update:modelValue', [...this.modelValue, value])
            this.newDomain = ''
        },

        removeAt (index) {
            const next = [...this.modelValue]
            next.splice(index, 1)
            this.$emit('update:modelValue', next)
        },

        updateAt (index, value) {
            const next = [...this.modelValue]
            next[index] = value
            this.$emit('update:modelValue', next)
        },

        placeholderFor (index) {
            if (this.rowPlaceholder) {
                return this.rowPlaceholder(index, this.modelValue[index])
            }
            return this.$t('certificate_domains.domain_row_placeholder')
        },

        errorFor (index) {
            return this.errors[index] ?? null
        },

        isValid (index) {
            return this.errorFor(index) === null ? null : false
        },

        flushPendingInput () {
            if (this.newDomain.trim() !== '') {
                this.commitNew()
            }
        }
    }
}
</script>
